// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null); // profiles table row
  const [loading, setLoading] = useState(true);

  // ── Fetch the profiles row for the logged-in user ──────────────────────────
  const fetchProfile = async (authUser) => {
    if (!authUser) { setProfile(null); return; }
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url, email, role')
      .eq('id', authUser.id)
      .maybeSingle();
    setProfile(data || null);
  };

  // ── Hydrate on mount, then listen for auth changes ─────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const authUser = session?.user ?? null;
      setUser(authUser);
      fetchProfile(authUser).finally(() => setLoading(false));
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const authUser = session?.user ?? null;
        setUser(authUser);
        fetchProfile(authUser);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // ── Called by AuthModal/LoginPage after supabase.auth.signIn resolves ──────
  // We don't actually need to do anything here — onAuthStateChange fires
  // automatically. But we keep this so existing call sites don't break.
  const loginUser = (_userData) => {
    // no-op: onAuthStateChange handles state update
  };

  // ── Sign out ───────────────────────────────────────────────────────────────
  const logoutUser = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  // ── Derived values every page needs ───────────────────────────────────────
  const isLoggedIn = !!user;
  const role       = profile?.role ?? user?.user_metadata?.role ?? null;
  const isPro      = role === 'pro';
  const isClient   = role === 'client' || (!isPro && isLoggedIn);

  // ── Merged user object pages expect ───────────────────────────────────────
  // Combines auth.users fields with profiles table fields so pages that
  // read user.full_name, user.avatar_url, user.email all work without changes.
  const mergedUser = user ? {
    ...user,
    id:         user.id,
    email:      profile?.email      ?? user.email,
    full_name:  profile?.full_name  ?? user.user_metadata?.full_name ?? '',
    avatar_url: profile?.avatar_url ?? null,
    role,
    // Legacy fields some pages still read
    name:       profile?.full_name  ?? user.user_metadata?.full_name ?? '',
  } : null;

  return (
    <AuthContext.Provider value={{
      user:       mergedUser,   // the object pages access via useAuth().user
      loading,
      isLoggedIn,
      isPro,
      isClient,
      role,
      loginUser,  // kept for backward compat, now a no-op
      logoutUser,
      refreshProfile: () => fetchProfile(user), // call this after profile edits
    }}>
      {/* Block render until we know auth state — prevents premature redirects */}
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};